import React, { useCallback, useEffect, useMemo } from 'react';
import { SliderConfig } from '@kemu-io/kemu-core/types';
import variablesManager, { getControlScopedVarName } from '@kemu-io/kemu-core/common/managers/variablesManager.js';
import { Slider } from 'antd';
import tinycolor from 'tinycolor2';
import { DataType } from '@kemu-io/hs-types';
import FieldLabel from '../FieldLabel/FieldLabel';
import styles from './SliderField.module.css';
import { ABORT_CHILD_DRAGGING } from '@common/constants';
import FormGroup from '@components/form-control/formGroup/formGroup';
import useForceUpdate from '@hooks/useForceReload';

type Props = {
  label: string;
  variableName: string;
  recipeId: string;
  thingRecipeId: string;
  widgetId: string;
  color?: string;
  disabled?: boolean;
  config: SliderConfig;
  settingsIndex: number;
  onUpdateConfig: (settingsIndex: number, newConfig: SliderConfig) => void;
}

const SliderField = (props: Props) => {
  const { variableName, widgetId, color, disabled, config, settingsIndex, onUpdateConfig } = props;
  const scopedVarName = getControlScopedVarName(variableName);
  const forceUpdate = useForceUpdate();

  const variableValue = variablesManager.getVariableValue({
    variableName: scopedVarName,
    readerWidgetId: widgetId,
  });

  const colors = useMemo(() => {
    if (!color) { return undefined; }
    const trackColor = tinycolor(color).darken(20).toString();
    const brightness = tinycolor(color).getBrightness();
    const isBrightColor = brightness > 200;

    return {
      trackColor,
      brightness,
      isBrightColor,
    };
  }, [color]);

  const onValueChange = useCallback((value: number) => {
    variablesManager.setVariableValue({
      variableName: scopedVarName,
      type: DataType.Number,
      value,
      setByWidgetId: widgetId,
    });

    onUpdateConfig(settingsIndex, {
      ...config,
      customValue: value,
    } as SliderConfig);
  }, [scopedVarName, widgetId, onUpdateConfig, settingsIndex, config]);

  useEffect(() => {
    const unsubscribe = variablesManager.onVariableValueChange({
      variableName: scopedVarName,
      type: DataType.Anything,
      widgetId: widgetId,
      onValueChange: forceUpdate,
    });

    return unsubscribe;
  }, [scopedVarName, widgetId, forceUpdate]);

  return (
    <div className={styles.Wrapper}>
      <FormGroup column={true} noMarginBottom>
        <FieldLabel color={props.color} label={props.label} noBottomMargin />
        <div className={`${styles.SliderField} ${ABORT_CHILD_DRAGGING}`}>
          <Slider
            disabled={disabled}
            handleStyle={{
              borderColor: colors?.trackColor
            }}
            trackStyle={{
              backgroundColor: colors?.trackColor
            }}
            step={config.step}
            value={variableValue?.value as number ??  config.customValue ?? config.defaultValue ?? 0}
            onChange={onValueChange}
            min={config.min}
            max={config.max}
          />
        </div>
      </FormGroup>
    </div>
  );
};

export default SliderField;
